import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import IDS from '../__tests__/ids';
import {
  AccessibilityWrapper,
  Icon,
  LoadingSpinner,
} from 'legacy-packages/ft-cb';
import { AssignedIconWithTooltip } from 'chameleon/ui-stack/ui/assessments/icons-with-tooltip';
import SectionContainer from '../SectionContainer';
import { AdminCoordNoSchoolsAlert, NoStudentsAlert } from '../NoDataAlerts';

/**
 * Show the teacher popover with section statistics.
 * This renders classes and expandable StudentsContainer component with who watched and who didnt watch AP Daily videos.
 * */
const AdminCoordPopover = ({
  toggleSchoolSectionContent,
  sections,
  openedSchool,
  assignedSectionIds,
  schoolStats,
  loading,
}) => {
  if (loading) {
    return <LoadingSpinner />;
  }

  if (!(schoolStats || []).length) {
    return (
      <div>
        <AdminCoordNoSchoolsAlert />
      </div>
    );
  }

  let hasNoClassStudentProgress;
  let noSections;
  if (schoolStats.length === 1) {
    const onlySectionsWithoutStudents = !!!sections.find(
      ({ totalStudents }) => totalStudents > 0,
    );
    noSections = !sections.length;
    hasNoClassStudentProgress = noSections || onlySectionsWithoutStudents;
  }

  const sectionClassName = cn('flex-column padded bottom-bordered', {
    'has-no-students': hasNoClassStudentProgress,
  });

  return (
    <Fragment>
      <div className="flex-vertical-align padded bottom-bordered">
        {(assignedSectionIds.length > 0 && (
          <Fragment>
            <span className="bold-text" data-test-id={IDS.ASSIGNED_LABEL}>
              Assigned to:
            </span>
            <span data-test-id={IDS.ASSIGNED_SECTIONS}>
              {assignedSectionIds.length}/{sections.length} sections
            </span>
          </Fragment>
        )) || (
          <span className="bold-text" data-test-id={IDS.UNASSIGNED_LABEL}>
            Unassigned
          </span>
        )}
      </div>
      {schoolStats.length === 1 &&
        sections.map(
          ({
            classId: sectionId,
            className: sectionName,
            assigned,
            totalStudents,
            watchedStudents,
          }) => (
            <div
              key={sectionId}
              className={sectionClassName}
              data-test-id={IDS.SECTION_ROW}
            >
              <div className="flex-vertical-align space-between">
                <div className="flex-vertical-align fixed-width header">
                  <AssignedIconWithTooltip
                    target={`admin-popover-assigned-icon-${sectionId}`}
                    disable={!assigned}
                    className={cn('assigned-icon', {
                      'not-visible': !assigned,
                    })}
                    data-test-id={IDS.ASSIGNED_ICON}
                  />
                  <div data-test-id={IDS.SECTION_NAME}>{sectionName}</div>
                </div>
                <div
                  className="flex-vertical-align fixed-width header unwatched"
                  data-test-id={IDS.UNWATCHED_COLUMN}
                >
                  <Icon name="person" />
                  <div data-test-id={IDS.UNWATCHED_COLUMN_TEXT}>
                    {totalStudents - watchedStudents} Unwatched
                  </div>
                </div>
                <div
                  className="flex-vertical-align fixed-width header watched"
                  data-test-id={IDS.WATCHED_COLUMN}
                >
                  <Icon name="person" />
                  <div data-test-id={IDS.WATCHED_COLUMN_TEXT}>
                    {watchedStudents} Watched
                  </div>
                </div>
              </div>
              {totalStudents ? null : (
                <div>
                  <NoStudentsAlert />
                </div>
              )}
            </div>
          ),
        )}
      {noSections && (
        <div>
          <AdminCoordNoSchoolsAlert />/
        </div>
      )}
      {schoolStats.length > 1 &&
        schoolStats.map(({ school, classProgress }) => {
          const isCurrentSchoolOpened = openedSchool === school;
          const watchedStudents = classProgress.reduce(
            (current, { watchedStudents: watched }) => current + watched,
            0,
          );
          const unwatchedStudents = classProgress.reduce(
            (current, { watchedStudents: watched, totalStudents: total }) =>
              current + (total - watched),
            0,
          );

          return (
            <div
              key={school}
              className="flex-column padded bottom-bordered"
              data-test-id={IDS.SECTION_ROW}
            >
              <div className="flex-vertical-align space-between">
                <div className="flex-vertical-align fixed-width header">
                  <div data-test-id={IDS.SECTION_NAME}>{school}</div>
                </div>
                <div
                  className="flex-vertical-align fixed-width header unwatched"
                  data-test-id={IDS.UNWATCHED_COLUMN}
                >
                  <Icon name="person" />
                  <div data-test-id={IDS.UNWATCHED_COLUMN_TEXT}>
                    {unwatchedStudents} Unwatched
                  </div>
                </div>
                <div
                  className="flex-vertical-align fixed-width header watched"
                  data-test-id={IDS.WATCHED_COLUMN}
                >
                  <Icon name="person" />
                  <div data-test-id={IDS.WATCHED_COLUMN_TEXT}>
                    {watchedStudents} Watched
                  </div>
                </div>
                <AccessibilityWrapper>
                  <div
                    aria-label={isCurrentSchoolOpened ? 'collapse' : 'expand'}
                    role="button"
                    className="flex-vertical-align handler-icon"
                    onClick={() => toggleSchoolSectionContent(school)}
                    data-test-id={IDS.TOGGLE_STUDENTS_CONTAINER}
                    tabIndex="0"
                  >
                    <Icon
                      name={isCurrentSchoolOpened ? 'minus' : 'plus'}
                      size="1.6rem"
                    />
                  </div>
                </AccessibilityWrapper>
              </div>
              <SectionContainer
                showContainer={isCurrentSchoolOpened}
                classProgress={classProgress || []}
              />
            </div>
          );
        })}
    </Fragment>
  );
};

AdminCoordPopover.displayName = 'AdminCoordPopover';

AdminCoordPopover.propTypes = {
  toggleSchoolSectionContent: PropTypes.func.isRequired,
  sections: PropTypes.array,
  schoolStats: PropTypes.array,
  openedSchool: PropTypes.string,
  assignedSectionIds: PropTypes.array,
  loading: PropTypes.bool,
};

AdminCoordPopover.defaultProps = {
  sections: [],
  schoolStats: [],
  openedSchool: '',
  assignedSectionIds: [],
  loading: false,
};

export default AdminCoordPopover;
